/**
 * The app's one message channel.
 *
 * The platform's undo stack ships no toast of its own — it calls whatever
 * notifier is installed. ToastLayer installs one that publishes here, so a
 * plan edit's "Undo" and a plain "Saved" travel the same path and can never
 * disagree about how a message looks.
 */

export interface Toast {
  id: number
  message: string
  /** Present when the action can be taken back. */
  undo?: () => void
  /** Errors stay until dismissed; everything else expires. */
  tone: 'plain' | 'error'
}

type Listener = (toasts: Toast[]) => void

let toasts: Toast[] = []
let listeners: Listener[] = []
let nextId = 1

const emit = () => {
  for (const l of listeners) l(toasts)
}

export function subscribeToasts(listener: Listener): () => void {
  listeners.push(listener)
  listener(toasts)
  return () => {
    listeners = listeners.filter((l) => l !== listener)
  }
}

export function pushToast(message: string, opts: { undo?: () => void; tone?: Toast['tone'] } = {}) {
  // One at a time. Stacked toasts over a thumb zone are a trap on a phone.
  toasts = [{ id: nextId++, message, undo: opts.undo, tone: opts.tone ?? 'plain' }]
  emit()
  return toasts[0]!.id
}

export function dismissToast(id: number) {
  toasts = toasts.filter((t) => t.id !== id)
  emit()
}

export function getToasts(): Toast[] {
  return toasts
}
